/**
 * Admin WeChat binding management hook (Issue #269).
 *
 * Wraps the typed `@jinzhuan/api-client` calls for the `/settings` 微信登录
 * 与绑定管理 section:
 *   - GET /admin/customer-wechat/bindings
 *   - GET /admin/customer-wechat/work-orders
 *   - GET /admin/customer-wechat/login-policy
 *   - POST /admin/customer-wechat/work-orders
 *   - POST /admin/customer-wechat/work-orders/{id}/submit
 *   - POST /admin/customer-wechat/work-orders/{id}/approve
 *   - POST /admin/customer-wechat/work-orders/{id}/reject
 *   - POST /admin/customer-wechat/work-orders/{id}/cancel
 *   - POST /admin/customer-wechat/work-orders/{id}/execute
 *   - PATCH /admin/customer-wechat/login-policy
 *
 * Red lines:
 *   - All data flows through the typed client; no hand-written fetch.
 *   - The hook never fabricates rows or success states on failures.
 *   - Mutations refresh the affected slices on success so the UI reflects
 *     server state, not optimistic in-memory edits.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  createApiClient,
  type AdminWechatBindingSummaryDto,
  type AdminWechatBindingWorkOrderDto,
  type AdminWechatLoginPolicyDto,
  type ApproveAdminWechatBindingWorkOrderRequest,
  type CancelAdminWechatBindingWorkOrderRequest,
  type CreateAdminWechatBindingWorkOrderRequest,
  type RejectAdminWechatBindingWorkOrderRequest,
  type SubmitAdminWechatBindingWorkOrderRequest,
  type UpdateAdminWechatLoginPolicyRequest,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const m = (err as { message?: string }).message;
    if (typeof m === 'string') return m;
  }
  return fallback;
}

export interface WechatBindingManagementReturn {
  readonly bindings: AsyncState<readonly AdminWechatBindingSummaryDto[]>;
  readonly bindingsTotal: number;
  readonly workOrders: AsyncState<readonly AdminWechatBindingWorkOrderDto[]>;
  readonly workOrdersTotal: number;
  readonly policy: AsyncState<AdminWechatLoginPolicyDto>;
  refresh: () => Promise<void>;
  refreshBindings: (filter?: BindingListFilter) => Promise<void>;
  refreshWorkOrders: (filter?: WorkOrderListFilter) => Promise<void>;
  refreshPolicy: () => Promise<void>;
  createWorkOrder: (
    body: CreateAdminWechatBindingWorkOrderRequest,
  ) => Promise<AdminWechatBindingWorkOrderDto>;
  submitWorkOrder: (
    id: string,
    body: SubmitAdminWechatBindingWorkOrderRequest,
  ) => Promise<AdminWechatBindingWorkOrderDto>;
  approveWorkOrder: (
    id: string,
    body: ApproveAdminWechatBindingWorkOrderRequest,
  ) => Promise<AdminWechatBindingWorkOrderDto>;
  rejectWorkOrder: (
    id: string,
    body: RejectAdminWechatBindingWorkOrderRequest,
  ) => Promise<AdminWechatBindingWorkOrderDto>;
  cancelWorkOrder: (
    id: string,
    body: CancelAdminWechatBindingWorkOrderRequest,
  ) => Promise<AdminWechatBindingWorkOrderDto>;
  executeWorkOrder: (id: string) => Promise<AdminWechatBindingWorkOrderDto>;
  updatePolicy: (
    body: UpdateAdminWechatLoginPolicyRequest,
  ) => Promise<AdminWechatLoginPolicyDto>;
}

export interface BindingListFilter {
  readonly customerAccountId?: string;
  readonly status?: AdminWechatBindingSummaryDto['status'];
}

export interface WorkOrderListFilter {
  readonly customerAccountId?: string;
  readonly status?: AdminWechatBindingWorkOrderDto['status'];
  readonly requestType?: AdminWechatBindingWorkOrderDto['requestType'];
}

export function useWechatBindingManagement(): WechatBindingManagementReturn {
  const [bindings, setBindings] = useState<
    AsyncState<readonly AdminWechatBindingSummaryDto[]>
  >({ data: null, loading: false, error: null });
  const [bindingsTotal, setBindingsTotal] = useState<number>(0);
  const [workOrders, setWorkOrders] = useState<
    AsyncState<readonly AdminWechatBindingWorkOrderDto[]>
  >({ data: null, loading: false, error: null });
  const [workOrdersTotal, setWorkOrdersTotal] = useState<number>(0);
  const [policy, setPolicy] = useState<AsyncState<AdminWechatLoginPolicyDto>>(
    {
      data: null,
      loading: false,
      error: null,
    },
  );

  const refreshBindings = useCallback(
    async (filter?: BindingListFilter) => {
      setBindings((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const { data, error } = await apiClient.GET(
          '/api/v1/admin/customer-wechat/bindings',
          {
            params: {
              query: {
                customerAccountId: filter?.customerAccountId,
                status: filter?.status,
                limit: 50,
              },
            },
          },
        );
        if (error) {
          setBindings({
            data: null,
            loading: false,
            error: pickErrorMessage(error, '加载微信绑定摘要失败'),
          });
          return;
        }
        setBindings({ data: data?.rows ?? [], loading: false, error: null });
        setBindingsTotal(data?.total ?? 0);
      } catch (err) {
        setBindings({
          data: null,
          loading: false,
          error: pickErrorMessage(err, '加载微信绑定摘要失败'),
        });
      }
    },
    [],
  );

  const refreshWorkOrders = useCallback(
    async (filter?: WorkOrderListFilter) => {
      setWorkOrders((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const { data, error } = await apiClient.GET(
          '/api/v1/admin/customer-wechat/work-orders',
          {
            params: {
              query: {
                customerAccountId: filter?.customerAccountId,
                status: filter?.status,
                requestType: filter?.requestType,
                limit: 50,
              },
            },
          },
        );
        if (error) {
          setWorkOrders({
            data: null,
            loading: false,
            error: pickErrorMessage(error, '加载微信绑定工单失败'),
          });
          return;
        }
        setWorkOrders({ data: data?.rows ?? [], loading: false, error: null });
        setWorkOrdersTotal(data?.total ?? 0);
      } catch (err) {
        setWorkOrders({
          data: null,
          loading: false,
          error: pickErrorMessage(err, '加载微信绑定工单失败'),
        });
      }
    },
    [],
  );

  const refreshPolicy = useCallback(async () => {
    setPolicy((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/admin/customer-wechat/login-policy',
      );
      if (error) {
        setPolicy({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载微信登录策略失败'),
        });
        return;
      }
      setPolicy({ data: data ?? null, loading: false, error: null });
    } catch (err) {
      setPolicy({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载微信登录策略失败'),
      });
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([refreshBindings(), refreshWorkOrders(), refreshPolicy()]);
  }, [refreshBindings, refreshWorkOrders, refreshPolicy]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createWorkOrder = useCallback(
    async (body: CreateAdminWechatBindingWorkOrderRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/admin/customer-wechat/work-orders',
        { body },
      );
      if (error || !data) {
        throw new Error(
          pickErrorMessage(error, '创建工单失败'),
        );
      }
      await Promise.all([refreshBindings(), refreshWorkOrders()]);
      return data;
    },
    [refreshBindings, refreshWorkOrders],
  );

  const submitWorkOrder = useCallback(
    async (id: string, body: SubmitAdminWechatBindingWorkOrderRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/admin/customer-wechat/work-orders/{id}/submit',
        { body, params: { path: { id } } },
      );
      if (error || !data) {
        throw new Error(
          pickErrorMessage(error, '提交工单失败'),
        );
      }
      await refreshWorkOrders();
      return data;
    },
    [refreshWorkOrders],
  );

  const approveWorkOrder = useCallback(
    async (id: string, body: ApproveAdminWechatBindingWorkOrderRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/admin/customer-wechat/work-orders/{id}/approve',
        { body, params: { path: { id } } },
      );
      if (error || !data) {
        throw new Error(
          pickErrorMessage(error, '审批通过失败'),
        );
      }
      await refreshWorkOrders();
      return data;
    },
    [refreshWorkOrders],
  );

  const rejectWorkOrder = useCallback(
    async (id: string, body: RejectAdminWechatBindingWorkOrderRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/admin/customer-wechat/work-orders/{id}/reject',
        { body, params: { path: { id } } },
      );
      if (error || !data) {
        throw new Error(
          pickErrorMessage(error, '驳回工单失败'),
        );
      }
      await refreshWorkOrders();
      return data;
    },
    [refreshWorkOrders],
  );

  const cancelWorkOrder = useCallback(
    async (id: string, body: CancelAdminWechatBindingWorkOrderRequest) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/admin/customer-wechat/work-orders/{id}/cancel',
        { body, params: { path: { id } } },
      );
      if (error || !data) {
        throw new Error(
          pickErrorMessage(error, '取消工单失败'),
        );
      }
      await refreshWorkOrders();
      return data;
    },
    [refreshWorkOrders],
  );

  const executeWorkOrder = useCallback(
    async (id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/admin/customer-wechat/work-orders/{id}/execute',
        { params: { path: { id } } },
      );
      if (error || !data) {
        throw new Error(
          pickErrorMessage(error, '执行工单失败'),
        );
      }
      await Promise.all([refreshBindings(), refreshWorkOrders()]);
      return data;
    },
    [refreshBindings, refreshWorkOrders],
  );

  const updatePolicy = useCallback(
    async (body: UpdateAdminWechatLoginPolicyRequest) => {
      const { data, error } = await apiClient.PATCH(
        '/api/v1/admin/customer-wechat/login-policy',
        { body },
      );
      if (error || !data) {
        throw new Error(pickErrorMessage(error, '更新微信登录策略失败'));
      }
      setPolicy({ data, loading: false, error: null });
      return data;
    },
    [],
  );

  return {
    bindings,
    bindingsTotal,
    workOrders,
    workOrdersTotal,
    policy,
    refresh,
    refreshBindings,
    refreshWorkOrders,
    refreshPolicy,
    createWorkOrder,
    submitWorkOrder,
    approveWorkOrder,
    rejectWorkOrder,
    cancelWorkOrder,
    executeWorkOrder,
    updatePolicy,
  };
}
